import { Col, ConfigProvider, DatePicker, Form, Input, Row, Select, Typography } from "antd";
import { useEffect } from "react";
import SDIModal from "../../../../components/ui/SDIModal";
import frFR from 'antd/locale/fr_FR';
import dayjs from "dayjs";
import { formatCNI, getEmptyConducteurForm } from "../../../../utils/personneData";

const { Option } = Select;

const ConducteurModal = ({ visible, onClose, conducteur, onSave, professions = [], enginIndex = null }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                ...getEmptyConducteurForm(),
                ...conducteur,
                telephone: conducteur?.telephone || conducteur?.contact?.[0] || '',
                dateDelivrance: conducteur?.dateDelivrance ? dayjs(conducteur.dateDelivrance) : null,
            });
        } else {
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, conducteur, enginIndex]);

    const saveConducteurHabituel = async () => {
        try {
            const values = await form.validateFields();
            // normaliser téléphone (8 chiffres) et pattern déjà vérifié par la rule
            const phone = String(values.telephone).replace(/\D/g, '').slice(0, 8);
            const payload = {
                ...values,
                telephone: phone,
                contact: [phone],
                dateDelivrance: values.dateDelivrance ? values.dateDelivrance.toISOString() : null,
            };

            if (payload.typePiece === 'CNI' && payload.numIdentite) {
                payload.numIdentite = formatCNI(payload.numIdentite);
            }

            onSave(payload);
            form.resetFields();
            onClose && onClose();
        } catch (err) {
            // validateFields déclenche l'affichage des erreurs sur les Form.Item
        }
    };

    return (
        <SDIModal
            title={`Conducteur habituel - Engin N°${enginIndex !== null ? enginIndex + 1 : ''}`}
            open={visible}
            onOk={saveConducteurHabituel}
            onCancel={() => {
                onClose && onClose();
                form.resetFields();
            }}
            width={800}
            okText="Enregistrer"
            cancelText="Annuler"
            zIndex={2000}
        >
            <ConfigProvider locale={frFR}>
                <div className="text-center mb-4">
                    <Typography.Text type="secondary">
                        Précisez le conducteur habituel si différent du souscripteur du contrat
                    </Typography.Text>
                </div>

                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Nom"
                                name="nom"
                                rules={[{ required: true, message: 'Le nom est requis' }]}
                            >
                                <Input placeholder="Nom du conducteur" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Prénom(s)"
                                name="prenom"
                                rules={[{ required: true, message: 'Le(s) prénom(s) est requis' }]}
                            >
                                <Input placeholder="Prénom(s) du conducteur" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Sexe"
                                name="genre"
                                rules={[{ required: true, message: 'Le sexe est requis' }]}
                            >
                                <Select>
                                    <Option value="MASCULIN">MASCULIN</Option>
                                    <Option value="FEMININ">FEMININ</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label="Profession" name="profession">
                                <Select placeholder="Sélectionner la profession" showSearch allowClear>
                                    <Option value="">Aucune</Option>
                                    {professions.map((prof, id) => (
                                        <Option key={id} value={prof.libelleParametre ? prof.libelleParametre : prof}>
                                            {prof.libelleParametre ? prof.libelleParametre : prof}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Téléphone"
                                name="telephone"
                                rules={[
                                    { required: true, message: 'Le téléphone est requis' },
                                    { pattern: /^(22|90|91|92|93|96|97|98|99|70|71|79)\d{6}$/, message: 'Numéro invalide' }
                                ]}
                            >
                                <Input placeholder="00000000" addonBefore="TG (+228)" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label="Email" name="email" rules={[{ type: 'email', message: 'Email invalide' }]}>
                                <Input placeholder="email@exemple.com" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Adresse"
                                name="adresse"
                                rules={[{ required: true, message: 'L\'adresse est requise' }]}
                            >
                                <Input placeholder="Adresse de résidence" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label="Type de pièce" name="typePiece">
                                <Select placeholder="Sélectionner le type" allowClear>
                                    <Option value="">Aucune</Option>
                                    <Option value="CNI">CNI</Option>
                                    <Option value="PASSEPORT">PASSEPORT</Option>
                                    <Option value="PERMIS">PERMIS DE CONDUIRE</Option>
                                    <Option value="ELECTEUR">CARTE D'ÉLECTEUR</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label="Numéro de la pièce" name="numIdentite">
                                <Input placeholder="Numéro de la pièce" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label="Date de délivrance" name="dateDelivrance">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </ConfigProvider>
        </SDIModal>
    );
};

export default ConducteurModal;